<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { subDays, fromUnixTime } from 'date-fns';
import { getUnixStartOfDay, getUnixEndOfDay } from 'helpers/DateHelper';
import { buildRatingsList } from './CsatFilterHelpers';
import ReportPeriodFilter from 'dashboard/components-next/polaris/reports/ReportPeriodFilter.vue';
import FilterSelect from 'dashboard/components-next/polaris/cards/FilterSelect.vue';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  parseReportURLParams,
  parseFilterURLParams,
  generateCompleteURLParams,
} from '../../helpers/reportFilterHelper';
import { DATE_RANGE_TYPES } from 'dashboard/components/ui/DatePicker/helpers/DatePickerHelper';

const props = defineProps({
  showTeamFilter: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['filterChange']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

// Initialize from URL params immediately
const urlParams = parseReportURLParams(route.query);
const urlFilters = parseFilterURLParams(route.query);

const initialDateRange =
  urlParams.from && urlParams.to
    ? [fromUnixTime(urlParams.from), fromUnixTime(urlParams.to)]
    : [subDays(new Date(), 6), new Date()];

const customDateRange = ref(initialDateRange);
const selectedDateRange = ref(urlParams.range || DATE_RANGE_TYPES.LAST_7_DAYS);

const appliedFilters = ref({
  user_ids: urlFilters.agent_id,
  inbox_id: urlFilters.inbox_id,
  team_id: urlFilters.team_id,
  rating: urlFilters.rating,
});

const agents = computed(() => store.getters['agents/getAgents']);
const inboxes = computed(() => store.getters['inboxes/getInboxes']);
const teams = computed(() => store.getters['teams/getTeams']);

const ratings = computed(() => buildRatingsList(t));

const from = computed(() => getUnixStartOfDay(customDateRange.value[0]));
const to = computed(() => getUnixEndOfDay(customDateRange.value[1]));

// 7-4 = A: one kit FilterSelect per filter, always on the bar, wrapping when there is no room (no "Add filter" menu,
// so no submenu to run off the screen). One value each, with search, as the woot has today.
const toOptions = list =>
  list.map(item => ({ value: item.id, label: item.name || item.title }));
const filters = computed(() =>
  [
    {
      key: 'user_ids',
      label: t('CSAT_REPORTS.FILTERS.AGENTS.LABEL'),
      options: toOptions(agents.value),
    },
    {
      key: 'inbox_id',
      label: t('CSAT_REPORTS.FILTERS.INBOXES.LABEL'),
      options: toOptions(inboxes.value),
    },
    props.showTeamFilter && {
      key: 'team_id',
      label: t('CSAT_REPORTS.FILTERS.TEAMS.LABEL'),
      options: toOptions(teams.value),
    },
    {
      key: 'rating',
      label: t('CSAT_REPORTS.FILTERS.RATINGS.LABEL'),
      options: ratings.value.map(item => ({
        value: item.id,
        label: item.name,
      })),
      searchable: false,
    },
  ].filter(Boolean)
);
// URL values come back as text; the options' ids are numbers.
const filterValue = key => {
  const value = appliedFilters.value[key];
  return value === null || value === undefined || value === ''
    ? ''
    : Number(value);
};

const hasActiveFilters = computed(() =>
  Object.values(appliedFilters.value).some(
    value => value !== null && value !== undefined && value !== ''
  )
);

const updateURLParams = () => {
  const params = generateCompleteURLParams({
    from: from.value,
    to: to.value,
    range: selectedDateRange.value,
    filters: {
      agent_id: appliedFilters.value.user_ids,
      inbox_id: appliedFilters.value.inbox_id,
      team_id: appliedFilters.value.team_id,
      rating: appliedFilters.value.rating,
    },
  });
  router.replace({ query: params });
};

const emitChange = () => {
  updateURLParams();
  emit('filterChange', {
    from: from.value,
    to: to.value,
    selectedAgents: appliedFilters.value.user_ids
      ? [{ id: appliedFilters.value.user_ids }]
      : [],
    selectedInbox: appliedFilters.value.inbox_id
      ? { id: appliedFilters.value.inbox_id }
      : null,
    selectedTeam: appliedFilters.value.team_id
      ? { id: appliedFilters.value.team_id }
      : null,
    selectedRating: appliedFilters.value.rating
      ? { value: appliedFilters.value.rating }
      : null,
  });
};

const setFilter = (key, value) => {
  appliedFilters.value[key] = value === '' ? null : value;
  emitChange();
};

const clearAllFilters = () => {
  appliedFilters.value = {
    user_ids: null,
    inbox_id: null,
    team_id: null,
    rating: null,
  };
  emitChange();
};

const onDateRangeChange = value => {
  const [startDate, endDate, rangeType] = value;
  customDateRange.value = [startDate, endDate];
  selectedDateRange.value = rangeType || DATE_RANGE_TYPES.CUSTOM_RANGE;
  emitChange();
};

onMounted(() => {
  emitChange();
});
</script>

<template>
  <div
    class="flex w-full flex-wrap items-center gap-2"
    role="group"
    :aria-label="$t('POLARIS_REPORTS.FILTERS.LABEL')"
    data-report-filters
  >
    <ReportPeriodFilter
      v-model:date-range="customDateRange"
      v-model:range-type="selectedDateRange"
      @date-range-changed="onDateRangeChange"
    />
    <FilterSelect
      v-for="filter in filters"
      :key="filter.key"
      :model-value="filterValue(filter.key)"
      :label="filter.label"
      :options="filter.options"
      :searchable="filter.searchable !== false"
      :data-filter="filter.key"
      @update:model-value="value => setFilter(filter.key, value)"
    />
    <Button
      v-if="hasActiveFilters"
      variant="ghost"
      size="sm"
      @click="clearAllFilters"
    >
      {{ $t('CSAT_REPORTS.FILTERS.CLEAR_ALL') }}
    </Button>
  </div>
</template>
